import React, { useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import apiClient from '../../services/apiClient';

const REASON_LABEL = {
  'Client negasit': 'Client negasit',
  'Tip necunoscut': 'Tip marca necunoscut',
  'Duplicat':       'Duplicat (exista deja)',
  'Fisier negasit': 'Fisier negasit',
  'Eroare citire fisier': 'Eroare citire fisier',
};

export default function ImportMarciModal({ onClose }) {
  const inputRef             = useRef(null);
  const [files, setFiles]    = useState([]);
  const [running, setRunning] = useState(false);
  const [result, setResult]  = useState(null);
  const [error, setError]    = useState(null);

  const handleFiles = (e) => {
    const chosen = Array.from(e.target.files).filter(f => f.name.endsWith('.xlsx'));
    setFiles(chosen);
    setResult(null);
    setError(null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const dropped = Array.from(e.dataTransfer.files).filter(f => f.name.endsWith('.xlsx'));
    setFiles(dropped);
    setResult(null);
    setError(null);
  };

  const handleImport = async () => {
    if (!files.length) return;
    setRunning(true);
    setResult(null);
    setError(null);
    try {
      const form = new FormData();
      files.forEach(f => form.append('files', f));
      const res = await apiClient.post('/import-marci/', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Eroare la import');
    } finally {
      setRunning(false);
    }
  };

  const nonDupSkipped = result ? result.skipped.filter(s => s.reason !== 'Duplicat') : [];
  const dupCount      = result ? result.total_duplicates : 0;

  return ReactDOM.createPortal(
    <div className="modal-overlay" style={{ zIndex: 10000 }}>
      <div className="modal-box fm-modal" style={{ maxWidth: 560 }}>
        <div className="modal-sticky-header fm-header">
          <div className="fm-title-row">
            <span className="fm-title">Importa Marci</span>
            <button className="modal-close-btn" onClick={onClose} type="button">✕</button>
          </div>
        </div>

        <div className="modal-scroll-body" style={{ padding: '20px 24px' }}>
          {!result && (
            <>
              <div
                className="import-drop-zone"
                onDragOver={e => e.preventDefault()}
                onDrop={handleDrop}
                onClick={() => inputRef.current?.click()}
              >
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 8, color: 'var(--muted)' }}>
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="17 8 12 3 7 8"/>
                  <line x1="12" y1="3" x2="12" y2="15"/>
                </svg>
                <p style={{ margin: 0, fontWeight: 600, color: 'var(--text)' }}>
                  {files.length ? `${files.length} fisier${files.length !== 1 ? 'e' : ''} selectat${files.length !== 1 ? 'e' : ''}` : 'Trage fisierele aici sau apasa pentru a selecta'}
                </p>
                <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--muted)' }}>Accepta doar fisiere .xlsx (export TMView)</p>
                <input ref={inputRef} type="file" accept=".xlsx" multiple style={{ display: 'none' }} onChange={handleFiles} />
              </div>

              {files.length > 0 && (
                <ul className="import-file-list">
                  {files.map((f, i) => (
                    <li key={i}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                      <span>{f.name}</span>
                    </li>
                  ))}
                </ul>
              )}

              {error && <p className="import-error">{error}</p>}

              <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  className="btn btn-primary btn-md"
                  onClick={handleImport}
                  disabled={running || !files.length}
                  type="button"
                >
                  {running ? 'Se importa...' : 'Importa'}
                </button>
              </div>
            </>
          )}

          {result && (
            <div className="import-result">
              <div className="import-stats">
                <div className="import-stat import-stat-ok">
                  <span className="import-stat-num">{result.total_imported}</span>
                  <span className="import-stat-lbl">Importate</span>
                </div>
                <div className="import-stat import-stat-info">
                  <span className="import-stat-num">{result.total_logo_added}</span>
                  <span className="import-stat-lbl">Logo adaugat</span>
                </div>
                <div className="import-stat import-stat-muted">
                  <span className="import-stat-num">{dupCount}</span>
                  <span className="import-stat-lbl">Duplicate</span>
                </div>
                {nonDupSkipped.length > 0 && (
                  <div className="import-stat import-stat-warn">
                    <span className="import-stat-num">{nonDupSkipped.length}</span>
                    <span className="import-stat-lbl">Neimportate</span>
                  </div>
                )}
              </div>

              {nonDupSkipped.length > 0 && (
                <>
                  <p style={{ fontWeight: 600, marginBottom: 8, marginTop: 16 }}>Neimportate — motive:</p>
                  <div className="import-skip-table">
                    <div className="import-skip-head">
                      <span>Rand</span><span>Marca</span><span>Motiv</span><span>Detaliu</span>
                    </div>
                    {nonDupSkipped.map((s, i) => (
                      <div key={i} className="import-skip-row">
                        <span>{s.row}</span>
                        <span title={s.name}>{s.name}</span>
                        <span>{REASON_LABEL[s.reason] || s.reason}</span>
                        <span title={s.detail}>{s.detail}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}

              <div style={{ marginTop: 20, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button className="btn btn-secondary btn-md" onClick={() => { setResult(null); setFiles([]); }} type="button">
                  Import nou
                </button>
                <button className="btn btn-primary btn-md" onClick={onClose} type="button">
                  Inchide
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
